import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Mic, Square, Camera, X, Loader2, Wand2 } from 'lucide-react';

import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_API_URL;
const MAX_SECONDS = 180;
const MAX_PHOTOS = 3;
const MAX_EDGE = 1600;

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return null;
  const options = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  return options.find((m) => MediaRecorder.isTypeSupported?.(m)) || '';
}

/** Downscale a phone photo so uploads stay small; falls back to the original. */
export async function shrinkImage(file, maxEdge = MAX_EDGE) {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < 1.5 * 1024 * 1024 && /jpe?g|png|webp/.test(file.type)) return file;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    return blob ? new File([blob], 'photo.jpg', { type: 'image/jpeg' }) : file;
  } catch {
    return file;
  }
}

/**
 * AIMediaCapture — record a voice note on site and/or attach up to 3 photos;
 * the backend turns them into a job description which is handed back via
 * `onDescription` so the user can review it before generating the draft.
 */
export function AIMediaCapture({ disabled, language, onDescription }) {
  const { t } = useTranslation('quotes');
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [audio, setAudio] = useState(null);       // Blob
  const [photos, setPhotos] = useState([]);       // { file, url }
  const [busy, setBusy] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);
  const canRecord = typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && pickMimeType() !== null;

  useEffect(() => () => {
    clearInterval(timerRef.current);
    recorderRef.current?.stream?.getTracks().forEach((track) => track.stop());
    photos.forEach((p) => URL.revokeObjectURL(p.url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    setRecording(false);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        if (blob.size) setAudio(blob);
      };
      recorderRef.current = recorder;
      recorder.start();
      setAudio(null);
      setSeconds(0);
      setRecording(true);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_SECONDS) stopRecording();
          return s + 1;
        });
      }, 1000);
    } catch {
      toast.error(t('ai.media.micDenied'));
    }
  };

  const addPhotos = async (fileList) => {
    const room = MAX_PHOTOS - photos.length;
    const picked = Array.from(fileList || []).slice(0, Math.max(0, room));
    if (fileList?.length > room) toast.message(t('ai.media.maxPhotos', { count: MAX_PHOTOS }));
    const prepared = await Promise.all(picked.map(shrinkImage));
    setPhotos((prev) => [...prev, ...prepared.map((file) => ({ file, url: URL.createObjectURL(file) }))]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[idx]?.url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const extract = async () => {
    if (!audio && !photos.length) return;
    setBusy(true);
    try {
      const form = new FormData();
      if (audio) {
        const ext = (audio.type || '').includes('mp4') ? 'm4a' : (audio.type || '').includes('ogg') ? 'ogg' : 'webm';
        form.append('audio', audio, `note.${ext}`);
      }
      photos.forEach((p, i) => form.append('images', p.file, p.file.name || `photo-${i + 1}.jpg`));
      if (language) form.append('language', language);
      const { data } = await axios.post(`${API_URL}/api/quote/ai/draft/media`, form);
      onDescription?.(data.description || '');
      setAudio(null);
      photos.forEach((p) => URL.revokeObjectURL(p.url));
      setPhotos([]);
      toast.success(t('ai.media.done'));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || t('ai.media.failed')));
    } finally {
      setBusy(false);
    }
  };

  const mmss = `${String(Math.floor(seconds / 60)).padStart(1, '0')}:${String(seconds % 60).padStart(2, '0')}`;

  return (
    <div className="mt-2 rounded-xl border border-dashed border-indigo-200 bg-indigo-50/40 p-3" data-testid="ai-media-capture">
      <p className="text-xs text-[#475569] mb-2">{t('ai.media.hint')}</p>
      <div className="flex flex-wrap items-center gap-2">
        {canRecord && (recording ? (
          <Button type="button" size="sm" variant="outline" onClick={stopRecording}
            className="gap-1.5 border-rose-300 text-rose-700" data-testid="ai-media-stop">
            <Square className="w-3.5 h-3.5 fill-current" /> {t('ai.media.stop')} · {mmss}
          </Button>
        ) : (
          <Button type="button" size="sm" variant="outline" onClick={startRecording} disabled={disabled || busy}
            className="gap-1.5" data-testid="ai-media-record">
            <Mic className="w-3.5 h-3.5" /> {audio ? t('ai.media.reRecord') : t('ai.media.record')}
          </Button>
        ))}
        <Button type="button" size="sm" variant="outline" disabled={disabled || busy || photos.length >= MAX_PHOTOS}
          onClick={() => fileInputRef.current?.click()} className="gap-1.5" data-testid="ai-media-photo">
          <Camera className="w-3.5 h-3.5" /> {t('ai.media.addPhoto')}
        </Button>
        <input ref={fileInputRef} type="file" accept="image/*" capture="environment" multiple hidden
          onChange={(e) => addPhotos(e.target.files)} />
        {audio && !recording && (
          <span className="text-xs text-emerald-700" data-testid="ai-media-audio-ready">{t('ai.media.audioReady')}</span>
        )}
      </div>
      {!!photos.length && (
        <div className="mt-2 flex gap-2">
          {photos.map((p, i) => (
            <div key={p.url} className="relative">
              <img src={p.url} alt="" className="h-16 w-16 rounded-lg object-cover border border-white shadow-sm" />
              <button type="button" onClick={() => removePhoto(i)} aria-label={t('ai.media.removePhoto')}
                className="absolute -top-1.5 -right-1.5 rounded-full bg-white p-0.5 shadow">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      {(audio || photos.length > 0) && !recording && (
        <Button type="button" size="sm" onClick={extract} disabled={busy || disabled}
          className="mt-2 gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="ai-media-extract">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
          {busy ? t('ai.media.working') : t('ai.media.extract')}
        </Button>
      )}
    </div>
  );
}

export default AIMediaCapture;
